import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PhoneCall,
  PhoneIncoming,
  PhoneOff,
  PhoneMissed,
  Clock,
  Search,
  Filter,
  RefreshCw,
  AlertTriangle,
  XCircle,
  MessageSquare,
  CheckCircle2,
  Calendar,
  CalendarCheck,
  Edit3,
  X,
  Check,
  UserCheck,
  FileText
} from 'lucide-react';
import api from '../../services/api';
import { saveCallDisposition } from '../../services/exotelApi';
import { useSocket } from '../../context/SocketContext';

const STATUS_CONFIG = {
  'call-successful': { icon: PhoneCall, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Call was successful' },
  'client-hungup-during-call': { icon: PhoneOff, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-200', label: 'Client hung-up during call' },
  'client-hungup-before-connecting': { icon: PhoneOff, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Client hung-up before connecting' },
  'no-answer': { icon: PhoneMissed, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'No user answered' },
  'no-user-answered': { icon: PhoneMissed, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'No user answered' },
  'client-unanswered': { icon: PhoneMissed, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Client unanswered' },
  busy: { icon: PhoneOff, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200', label: 'User was busy' },
  'in-progress': { icon: PhoneCall, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', label: 'Call in progress' },
  ringing: { icon: PhoneIncoming, color: 'text-indigo-600', bg: 'bg-indigo-50 border-indigo-200', label: 'Ringing' },
  waiting: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Waiting' },
  failed: { icon: PhoneOff, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Failed' },

  // Fallbacks for legacy/existing records
  completed: { icon: PhoneCall, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Call was successful' },
  answered: { icon: PhoneCall, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Call was successful' },
  cancelled: { icon: PhoneOff, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Client hung-up before connecting' },
  'call-fatal': { icon: PhoneOff, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Client hung-up before connecting' },
  missed: { icon: PhoneMissed, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'No user answered' },
  rejected: { icon: PhoneOff, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-200', label: 'Rejected' },
  queued: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', label: 'Queued' },
  initiated: { icon: PhoneCall, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', label: 'Initiated' },
};

const DISPOSITION_CONFIG = {
  pending: {
    label: 'Pending Callback',
    badge: 'bg-amber-50 text-amber-800 border-amber-300/80',
    dot: 'bg-amber-500',
    icon: Clock
  },
  callback_completed: {
    label: 'Callback Completed',
    badge: 'bg-emerald-50 text-emerald-800 border-emerald-300/80',
    dot: 'bg-emerald-500',
    icon: CheckCircle2
  },
  resolved: {
    label: 'Resolved',
    badge: 'bg-emerald-50 text-emerald-800 border-emerald-300/80',
    dot: 'bg-emerald-500',
    icon: CheckCircle2
  },
  follow_up_needed: {
    label: 'Follow-up Needed',
    badge: 'bg-blue-50 text-blue-800 border-blue-300/80',
    dot: 'bg-blue-500',
    icon: Calendar
  },
  appointment_booked: {
    label: 'Appointment Booked',
    badge: 'bg-purple-50 text-purple-800 border-purple-300/80',
    dot: 'bg-purple-500',
    icon: CalendarCheck
  },
  not_interested: {
    label: 'Not Interested',
    badge: 'bg-slate-100 text-slate-700 border-slate-300/80',
    dot: 'bg-slate-400',
    icon: XCircle
  },
  invalid_number: {
    label: 'Invalid / Wrong No.',
    badge: 'bg-rose-50 text-rose-800 border-rose-300/80',
    dot: 'bg-rose-500',
    icon: PhoneOff
  }
};

const getEffectiveDisposition = (log) => {
  if (log.disposition && DISPOSITION_CONFIG[log.disposition]) {
    return log.disposition;
  }
  const isAnswered = ['call-successful', 'completed', 'answered'].includes(log.status);
  return isAnswered ? 'resolved' : 'pending';
};

const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const formatPhone = (phone) => {
  if (!phone) return '—';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    const local = cleaned.slice(2);
    return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  }
  return phone;
};

export default function CallAnalytics() {
  const { socket } = useSocket() || {};
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ direction: '', status: '', disposition: '', search: '' });

  // Disposition Update Modal State
  const [selectedLog, setSelectedLog] = useState(null);
  const [dispForm, setDispForm] = useState({
    disposition: 'callback_completed',
    dispositionNotes: '',
    callReason: ''
  });
  const [savingDisp, setSavingDisp] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState(null);

  const fetchLogs = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/exotel/calls', {
        params: {
          direction: filters.direction || undefined,
          status: filters.status || undefined,
          limit: 100,
          _t: Date.now()
        }
      });
      setLogs(data.data || []);
    } catch (err) {
      if (!isBackground) setError(err?.response?.data?.message || 'Failed to load call history');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [filters.direction, filters.status]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Real-time socket updates when any call starts, updates state, or ends
  useEffect(() => {
    if (!socket) return;

    const handleUpdate = (data) => {
      if (data?.callSid || data?.callLogId) {
        setLogs(prev => {
          const exists = prev.some(l => (data.callSid && l.callSid === data.callSid) || (data.callLogId && l._id === data.callLogId));
          if (exists) {
            return prev.map(l => {
              if ((data.callSid && l.callSid === data.callSid) || (data.callLogId && l._id === data.callLogId)) {
                return {
                  ...l,
                  status: data.status || l.status,
                  duration: data.duration !== undefined ? data.duration : l.duration,
                  recordingUrl: data.recordingUrl || l.recordingUrl,
                  assignedAgent: data.assignedAgent || l.assignedAgent,
                  to: data.to || l.to,
                  disposition: data.disposition || l.disposition,
                  dispositionNotes: data.dispositionNotes || l.dispositionNotes,
                  dispositionUpdatedBy: data.dispositionUpdatedBy || l.dispositionUpdatedBy,
                  dispositionUpdatedAt: data.dispositionUpdatedAt || l.dispositionUpdatedAt,
                  callReason: data.callReason || l.callReason,
                  notes: data.notes || l.notes,
                  ...(data.data || {})
                };
              }
              return l;
            });
          }

          const newRow = {
            _id: data.callLogId || Date.now().toString(),
            callSid: data.callSid,
            direction: data.direction || 'inbound',
            from: data.from || data.callerNumber || '—',
            to: data.to || data.assignedAgent?.phone || '—',
            status: data.status || 'ringing',
            disposition: 'pending',
            duration: data.duration || 0,
            assignedAgent: data.assignedAgent || null,
            lead: data.lead || (data.patientName ? { patientName: data.patientName } : null),
            createdAt: data.timestamp || new Date().toISOString()
          };
          return [newRow, ...prev];
        });
      }
      fetchLogs(true);
    };

    socket.on('call_ended', handleUpdate);
    socket.on('incoming_call', handleUpdate);
    socket.on('call_status_updated', handleUpdate);
    return () => {
      socket.off('call_ended', handleUpdate);
      socket.off('incoming_call', handleUpdate);
      socket.off('call_status_updated', handleUpdate);
    };
  }, [socket, fetchLogs]);

  // Continuous live auto-refresh
  useEffect(() => {
    const hasActiveCall = logs.some(l => ['waiting', 'ringing', 'in-progress'].includes(l.status));
    const intervalMs = hasActiveCall ? 2500 : 6000;

    const timer = setInterval(() => {
      fetchLogs(true);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [logs, fetchLogs]);

  // Handle open disposition modal
  const handleOpenDispositionModal = (log) => {
    setSelectedLog(log);
    const effDisp = getEffectiveDisposition(log);
    setDispForm({
      disposition: effDisp === 'pending' ? 'callback_completed' : effDisp,
      dispositionNotes: log.dispositionNotes || log.notes || '',
      callReason: log.callReason || ''
    });
    setSaveSuccessMsg(null);
  };

  // Handle save disposition
  const handleSaveDisposition = async (e) => {
    e.preventDefault();
    if (!selectedLog) return;

    setSavingDisp(true);
    try {
      const res = await saveCallDisposition(selectedLog._id, {
        disposition: dispForm.disposition,
        dispositionNotes: dispForm.dispositionNotes.trim(),
        notes: dispForm.dispositionNotes.trim(),
        callReason: dispForm.callReason.trim()
      });

      // Optimistically update local logs
      const updatedItem = res?.data || {
        ...selectedLog,
        disposition: dispForm.disposition,
        dispositionNotes: dispForm.dispositionNotes.trim(),
        notes: dispForm.dispositionNotes.trim(),
        callReason: dispForm.callReason.trim(),
        dispositionUpdatedAt: new Date().toISOString()
      };

      setLogs(prev => prev.map(l => l._id === selectedLog._id ? { ...l, ...updatedItem } : l));
      setSaveSuccessMsg('Disposition updated successfully!');
      setTimeout(() => {
        setSelectedLog(null);
        setSaveSuccessMsg(null);
      }, 1000);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to save disposition');
    } finally {
      setSavingDisp(false);
    }
  };

  // Client-side filtering for Search and Disposition filter
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // Disposition filter
      if (filters.disposition) {
        const eff = getEffectiveDisposition(log);
        if (eff !== filters.disposition) return false;
      }

      // Search filter (phone, name, notes, reason)
      if (filters.search) {
        const q = filters.search.toLowerCase().trim();
        const fromNum = (log.from || '').toLowerCase();
        const toNum = (log.to || '').toLowerCase();
        const pName = (log.lead?.patientName || '').toLowerCase();
        const agentName = `${log.assignedAgent?.firstName || ''} ${log.assignedAgent?.lastName || ''}`.toLowerCase();
        const reason = (log.callReason || '').toLowerCase();
        const notes = (log.dispositionNotes || log.notes || '').toLowerCase();

        if (
          !fromNum.includes(q) &&
          !toNum.includes(q) &&
          !pName.includes(q) &&
          !agentName.includes(q) &&
          !reason.includes(q) &&
          !notes.includes(q)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [logs, filters.disposition, filters.search]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2.5">
            <PhoneCall className="text-indigo-600" size={26} />
            Call Analytics & Disposition
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Real-time telephony call logs, recordings, and CRM follow-up resolution status
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap w-full sm:w-auto">
          {/* Search box */}
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input
              type="text"
              placeholder="Search phone, patient..."
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-xs bg-white text-slate-700 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
            {filters.search && (
              <button
                onClick={() => setFilters({ ...filters, search: '' })}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Direction Filter */}
          <select
            value={filters.direction}
            onChange={e => setFilters({ ...filters, direction: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white text-slate-700 outline-none focus:border-indigo-500 font-medium"
          >
            <option value="">All Directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>

          {/* Call Status Filter */}
          <select
            value={filters.status}
            onChange={e => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white text-slate-700 outline-none focus:border-indigo-500 font-medium"
          >
            <option value="">All Call Statuses</option>
            <option value="call-successful">Call was successful</option>
            <option value="no-answer">No user answered (Missed)</option>
            <option value="client-unanswered">Client unanswered</option>
            <option value="client-hungup-during-call">Client hung-up during call</option>
            <option value="client-hungup-before-connecting">Client hung-up before connecting</option>
            <option value="busy">User was busy</option>
            <option value="in-progress">Call in progress</option>
            <option value="ringing">Ringing</option>
            <option value="failed">Failed</option>
          </select>

          {/* Follow-up Status Filter */}
          <select
            value={filters.disposition}
            onChange={e => setFilters({ ...filters, disposition: e.target.value })}
            className="px-3 py-2 border border-indigo-200 rounded-xl text-xs bg-indigo-50/50 text-indigo-900 font-medium outline-none focus:border-indigo-500"
          >
            <option value="">All Follow-up Statuses</option>
            <option value="pending">🟡 Pending Callback</option>
            <option value="callback_completed">🟢 Callback Completed</option>
            <option value="resolved">🟢 Resolved</option>
            <option value="follow_up_needed">🔵 Follow-up Needed</option>
            <option value="appointment_booked">🟣 Appointment Booked</option>
            <option value="not_interested">⚪ Not Interested</option>
            <option value="invalid_number">🔴 Invalid / Wrong No.</option>
          </select>

          <button
            onClick={() => fetchLogs()}
            disabled={loading}
            title="Refresh logs"
            className="p-2 border border-slate-200 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Main Table */}
      {error ? (
        <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6 text-center">
          <p className="text-rose-600 font-medium">{error}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-slate-500 uppercase">Time & Direction</th>
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-slate-500 uppercase">Participants</th>
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-slate-500 uppercase">Call Status</th>
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-slate-500 uppercase">Follow-up Status</th>
                  <th className="py-3 px-4 text-xs font-semibold tracking-wide text-slate-500 uppercase text-right">Duration & Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.length === 0 && !loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-slate-400">
                      No matching call records found.
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map(log => {
                    const cfg = STATUS_CONFIG[log.status] || {
                      icon: PhoneCall,
                      color: 'text-emerald-500',
                      bg: 'bg-emerald-500/10 border-emerald-500/20',
                      label: log.status ? (log.status.charAt(0).toUpperCase() + log.status.slice(1).replace(/-/g, ' ')) : 'Call was successful'
                    };
                    const Icon = cfg.icon;

                    const effDispKey = getEffectiveDisposition(log);
                    const dispCfg = DISPOSITION_CONFIG[effDispKey] || DISPOSITION_CONFIG.pending;
                    const DispIcon = dispCfg.icon;

                    const hasNotes = Boolean(log.dispositionNotes || log.notes);
                    const hasUpdater = Boolean(log.dispositionUpdatedBy?.firstName);

                    return (
                      <tr key={log._id} className="hover:bg-slate-50/60 transition-colors">
                        {/* 1. Time & Direction */}
                        <td className="py-3.5 px-4 whitespace-nowrap align-top">
                          <p className="text-sm font-medium text-slate-800">{formatTime(log.createdAt)}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            {log.direction === 'inbound' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200/60">
                                <PhoneIncoming size={11} className="text-indigo-600" />
                                Inbound
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                                <PhoneCall size={11} className="text-emerald-600" />
                                Outbound
                              </span>
                            )}
                          </div>
                        </td>

                        {/* 2. Participants */}
                        <td className="py-3.5 px-4 align-top">
                          <p className="text-sm font-medium text-slate-700">
                            <span className="text-slate-400 font-normal text-xs">From: </span>
                            {log.direction === 'outbound' && log.initiatedBy ? (
                              <span className="text-slate-800 font-medium">
                                {log.initiatedBy.firstName} {log.initiatedBy.lastName}{' '}
                                <span className="font-mono text-slate-500 text-xs font-normal">
                                  ({formatPhone(log.from)})
                                </span>
                              </span>
                            ) : log.lead?.patientName ? (
                              <span className="text-slate-800 font-medium">
                                {log.lead.patientName}{' '}
                                <span className="font-mono text-slate-500 text-xs font-normal">
                                  ({formatPhone(log.from)})
                                </span>
                              </span>
                            ) : (
                              <span className="font-mono text-slate-800">{formatPhone(log.from)}</span>
                            )}
                          </p>
                          <p className="text-sm text-slate-700 mt-0.5">
                            <span className="text-slate-400 font-normal text-xs">To: </span>
                            {log.assignedAgent ? (
                              <span className="font-semibold text-indigo-900">
                                {log.assignedAgent.firstName} {log.assignedAgent.lastName || ''}{' '}
                                <span className="font-mono text-slate-500 text-xs font-normal">
                                  ({formatPhone(log.assignedAgent.phone || log.to)})
                                </span>
                              </span>
                            ) : log.direction === 'outbound' && log.lead?.patientName ? (
                              <span className="text-slate-800 font-medium">
                                {log.lead.patientName}{' '}
                                <span className="font-mono text-slate-500 text-xs font-normal">
                                  ({formatPhone(log.to)})
                                </span>
                              </span>
                            ) : (
                              <span className="font-mono text-slate-700">{formatPhone(log.to)}</span>
                            )}
                          </p>

                          {/* Query Tag if logged */}
                          {log.callReason && (
                            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50/70 border border-indigo-200/80 text-[11px] font-medium text-indigo-700">
                                <MessageSquare size={10} />
                                Query: {log.callReason}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* 3. Call Status */}
                        <td className="py-3.5 px-4 align-top whitespace-nowrap">
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${cfg.bg} ${cfg.color}`}>
                            <Icon size={12} />
                            {cfg.label}
                          </div>
                        </td>

                        {/* 4. Follow-up Status */}
                        <td className="py-3.5 px-4 align-top">
                          <div className="flex flex-col items-start gap-1">
                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${dispCfg.badge}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${dispCfg.dot}`} />
                              <DispIcon size={12} />
                              {dispCfg.label}
                            </div>

                            {/* Who and When Updated */}
                            {hasUpdater && (
                              <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                                <UserCheck size={11} className="text-slate-400" />
                                <span>
                                  By <strong className="text-slate-700 font-medium">{log.dispositionUpdatedBy.firstName}</strong>
                                  {log.dispositionUpdatedAt ? ` on ${formatTime(log.dispositionUpdatedAt)}` : ''}
                                </span>
                              </p>
                            )}

                            {/* Notes/Remarks */}
                            {hasNotes && (
                              <p className="text-xs text-slate-600 mt-1 italic line-clamp-2 max-w-xs bg-slate-50 p-1.5 rounded border border-slate-100" title={log.dispositionNotes || log.notes}>
                                "{log.dispositionNotes || log.notes}"
                              </p>
                            )}
                          </div>
                        </td>

                        {/* 5. Duration & Action */}
                        <td className="py-3.5 px-4 text-right align-top whitespace-nowrap">
                          <p className="text-sm font-mono text-slate-700 font-medium">
                            {formatDuration(log.duration)}
                          </p>

                          <button
                            onClick={() => handleOpenDispositionModal(log)}
                            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200/80 rounded-lg transition-colors"
                            title="Update Follow-up Status"
                          >
                            <Edit3 size={12} />
                            <span>Update Status</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {loading && (
            <div className="flex justify-center p-8 border-t border-slate-100">
              <RefreshCw size={24} className="text-indigo-400 animate-spin" />
            </div>
          )}
        </div>
      )}

      {/* ─── MODAL: Update Follow-up Status ─────────────── */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-100">
                  <FileText size={18} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">Update Follow-up Status</h3>
                  <p className="text-xs text-slate-500">Record callback outcome and notes</p>
                </div>
              </div>
              <button
                onClick={() => !savingDisp && setSelectedLog(null)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveDisposition} className="p-5 overflow-y-auto space-y-4">
              {/* Call Context Summary Card */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 text-xs text-slate-600 space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Call Timestamp:</span>
                  <span className="font-medium text-slate-800">{formatTime(selectedLog.createdAt)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Caller / Patient:</span>
                  <span className="font-mono text-slate-800 font-semibold">
                    {selectedLog.lead?.patientName ? `${selectedLog.lead.patientName} (${formatPhone(selectedLog.from)})` : formatPhone(selectedLog.from)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1 border-t border-slate-200/60">
                  <span className="font-semibold text-slate-700">Call Status:</span>
                  <span className="inline-flex items-center gap-1 font-medium text-slate-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    {STATUS_CONFIG[selectedLog.status]?.label || selectedLog.status}
                  </span>
                </div>
              </div>

              {/* Success Message Banner */}
              {saveSuccessMsg && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  {saveSuccessMsg}
                </div>
              )}

              {/* Disposition Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Follow-up Status <span className="text-rose-500">*</span>
                </label>
                <select
                  value={dispForm.disposition}
                  onChange={e => setDispForm({ ...dispForm, disposition: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm font-medium bg-white text-slate-800 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  required
                >
                  <option value="callback_completed">🟢 Callback Completed / Resolved</option>
                  <option value="follow_up_needed">🔵 Follow-up Needed (Call Again Later)</option>
                  <option value="appointment_booked">🟣 Appointment Booked</option>
                  <option value="not_interested">⚪ Patient / Caller Not Interested</option>
                  <option value="invalid_number">🔴 Invalid / Wrong Phone Number</option>
                  <option value="pending">🟡 Pending Callback (Unresolved)</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Select the follow-up outcome for this call.
                </p>
              </div>

              {/* Inquiry / Reason Tag */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Call Reason / Medical Inquiry
                </label>
                <input
                  type="text"
                  placeholder="e.g. Knee Surgery inquiry, Consultation fee, Follow-up"
                  value={dispForm.callReason}
                  onChange={e => setDispForm({ ...dispForm, callReason: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Follow-up Notes / Remarks */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Follow-up Notes & Remarks
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Called customer back on 9 Sept. Inquired regarding Dr. Sharma availability. Scheduled an appointment for tomorrow 11 AM."
                  value={dispForm.dispositionNotes}
                  onChange={e => setDispForm({ ...dispForm, dispositionNotes: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 leading-relaxed resize-none"
                />
              </div>

              {/* Footer Actions */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  disabled={savingDisp}
                  onClick={() => setSelectedLog(null)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingDisp}
                  className="px-5 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-colors flex items-center gap-1.5 shadow-sm shadow-indigo-200 disabled:opacity-50"
                >
                  {savingDisp ? (
                    <>
                      <RefreshCw size={13} className="animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      <span>Save Status</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
